import lodashDebounce from 'lodash/debounce';
import ResizeObserverPolyfill from 'resize-observer-polyfill';
import { between } from '../../utils/format';
import { eventPosition } from '../../utils/event';
import isServer from '../../utils/isServer';

const axisList = ['vertical', 'horizontal'];
const dirProps = {
  vertical: { offset: 'offsetY', scroll: 'scrollTop', dir: 'down', dist: 'y', from: 'top' },
  horizontal: { offset: 'offsetX', scroll: 'scrollLeft', dir: 'right', dist: 'x', from: 'left' },
};
// const panOpts = {
//   prevent: true,
//   mouse: true,
//   mouseAllDir: true,
// };

export default {
  name: 'ScrollArea',
  props: {
    visible: {
      type: Boolean,
      default: null,
    },
    delay: {
      type: Number,
      default: 1000,
    },
    containerStyle: {
      type: [Object, String, Array],
      required: false,
      default: '',
    },
    guides: {
      type: Boolean,
      default: false,
    },
    denseGuideH: {
      type: Boolean,
      default: false,
    },
    denseGuideV: {
      type: Boolean,
      default: false,
    },
    allowContentOffset: {
      type: Boolean,
      default: false,
    },
    vThumbStartOffset: {
      type: Number,
      default: 0,
    },
    vThumbEndOffset: {
      type: Number,
      default: 0,
    },
    hThumbStartOffset: {
      type: Number,
      default: 0,
    },
    hThumbEndOffset: {
      type: Number,
      default: 0,
    },
  },
  data() {
    const _this = this;
    return {
      hover: false,
      guideHoverV: false,
      guideHoverH: false,
      container: {
        vertical: 0,
        horizontal: 0,
      },
      scroll: {
        vertical: {
          ref: null,
          position: 0,
          size: 0,
        },
        horizontal: {
          ref: null,
          position: 0,
          size: 0,
        },
      },
      targetRef: null,
      tempShowing: false,
      panning: false,
      panningAxis: null,
      timer: null,
      emitScroll: lodashDebounce(() => {
        const info = _this.getScroll();
        info.ref = _this.proxy;
        _this.$emit('scroll', info);
      }, 0),
      observerScrollArea: null,
      observerContent: null,
      mouseFrom: 0,
      isVisibleThumb: this.$props.visible,
      forceDenseGuideH: false,
      forceDenseGuideV: false,
    };
  },
  computed: {
    isDenseGuideH() {
      return this.denseGuideH || this.forceDenseGuideH;
    },
    isDenseGuideV() {
      return this.denseGuideV || this.forceDenseGuideV;
    },
    verticalPercentage() {
      const diff = this.scroll.vertical.size - this.container.vertical;
      if (diff <= 0) { return 0; }
      const p = between(this.scroll.vertical.position / diff, 0, 1);
      return Math.round(p * 10000) / 10000;
    },
    verticalThumbHidden() {
      return (
        (this.isVisibleThumb === null ? this.hover : this.isVisibleThumb) !== true &&
        this.tempShowing === false &&
        this.panning === false
      ) || this.scroll.vertical.size <= this.container.vertical + 1;
    },
    verticalThumbStart() {
      return this.verticalPercentage * (this.container.vertical - this.verticalThumbSize);
    },
    verticalThumbSize() {
      return Math.round(
        between(
          this.container.vertical * this.container.vertical / this.scroll.vertical.size,
          40,
          this.container.vertical,
        ),
      );
    },
    verticalStyle() {
      return {
        top: `${this.verticalThumbStart + this.vThumbStartOffset}px`,
        height: `${this.verticalThumbSize - (this.vThumbStartOffset + this.vThumbEndOffset)}px`,
      };
    },
    horizontalPercentage() {
      const diff = this.scroll.horizontal.size - this.container.horizontal;
      if (diff <= 0) { return 0; }
      const p = between(this.scroll.horizontal.position / diff, 0, 1);
      return Math.round(p * 10000) / 10000;
    },
    horizontalThumbHidden() {
      return (
        (this.isVisibleThumb === null ? this.hover : this.isVisibleThumb) !== true &&
        this.tempShowing === false &&
        this.panning === false
      ) || this.scroll.horizontal.size <= this.container.horizontal + 1;
    },
    horizontalThumbStart() {
      return this.horizontalPercentage * (this.container.horizontal - this.horizontalThumbSize);
    },
    horizontalThumbSize() {
      return Math.round(
        between(
          this.container.horizontal * this.container.horizontal / this.scroll.horizontal.size,
          40,
          this.container.horizontal,
        ),
      );
    },
    horizontalStyle() {
      return {
        left: `${this.horizontalThumbStart + this.hThumbStartOffset}px`,
        width: `${this.horizontalThumbSize - (this.hThumbStartOffset + this.hThumbEndOffset)}px`,
      };
    },
  },
  watch: {
    panning(value) {
      if (value) {
        document.body.style.cursor = 'grabbing';
        document.body.style.userSelect = 'none';
      } else {
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    },
  },
  mounted() {
    this.targetRef = this.$refs.container;
    this.scroll.vertical.ref = this.$refs.verticalThumb;
    this.scroll.horizontal.ref = this.$refs.horizontalThumb;

    this.observerContent = new ResizeObserverPolyfill(this.update);
    this.observerScrollArea = new ResizeObserverPolyfill(this.update);

    this.observerContent.observe(this.$refs.content);
    this.observerScrollArea.observe(this.$refs.scrollarea);

    this.update();
  },
  beforeDestroy() {
    if (this.observerContent) this.observerContent.disconnect();
    if (this.observerScrollArea) this.observerScrollArea.disconnect();
  },
  methods: {
    update() {
      this.updateContainer();
      this.updateScrollSize();
    },
    mousedownThumb(e, axis) {
      if (isServer()) return;
      this.panning = true;
      this.panningAxis = axis;

      document.addEventListener('mousemove', this.mouseMove);
      document.addEventListener('mouseup', this.mouseUp);

      const thumbStart = this[axis + 'ThumbStart'];
      const position = eventPosition(e);
      const clientRect = this.$el.getBoundingClientRect();
      const pos = position[dirProps[this.panningAxis].from] - clientRect[dirProps[axis].from];

      this.mouseFrom = pos - thumbStart;
    },
    mouseUp() {
      if (isServer()) return;
      this.panning = false;
      document.removeEventListener('mousemove', this.mouseMove);
      document.removeEventListener('mouseup', this.mouseUp);
    },
    mouseMove(e) {
      if (!this.panning || !this.panningAxis) return;

      const position = eventPosition(e);

      const clientRect = this.$el.getBoundingClientRect();
      const from = clientRect[dirProps[this.panningAxis].from];

      const scroll = this.scroll[this.panningAxis];
      const thumbSize = this[this.panningAxis + 'ThumbSize'];

      const contentSize = scroll.size;
      const containerSize = this.container[this.panningAxis] - thumbSize;

      const offsetStart = this.mouseFrom;

      const ratio = 1 - (this.container[this.panningAxis] / contentSize);

      const pos = position[dirProps[this.panningAxis].from];
      const posCorrected = ((pos - from) - offsetStart) * ratio;
      const posLimit = Math.min(containerSize, posCorrected);
      const posNorm = posLimit / containerSize;
      const posScrollContent = posNorm * contentSize;

      this.setScroll(posScrollContent, this.panningAxis);
      this.updateScroll();
    },
    getScrollPosition() {
      return {
        top: this.scroll.vertical.position,
        left: this.scroll.horizontal.position,
      };
    },
    onMousedown(e, axis) {
      const data = { ...this.scroll[axis] };

      if (data.thumbHidden !== true) {
        const position = eventPosition(e);

        const clientRect = this.$el.getBoundingClientRect();
        const from = clientRect[dirProps[axis].from];

        const scroll = this.scroll[axis];
        const thumbSize = this[axis + 'ThumbSize'];

        const contentSize = scroll.size;
        const containerSize = this.container[axis] - thumbSize;

        const offsetStart = (thumbSize / 2);

        const ratio = 1 - (this.container[axis] / contentSize);

        const pos = position[dirProps[axis].from];
        const posCorrected = ((pos - from) - offsetStart) * ratio;
        const posLimit = Math.min(containerSize, posCorrected);
        const posNorm = posLimit / containerSize;
        const posScrollContent = posNorm * contentSize;

        this.setScroll(posScrollContent, axis);
        this.updateScroll();

        // activate thumb pan
        if (data.ref !== null) {
          data.ref.dispatchEvent(new MouseEvent(e.type, e));
        }
      }
    },
    setScroll(offset, axis) {
      this.targetRef[dirProps[axis].scroll] = offset;
    },
    getScroll() {
      const info = {};

      axisList.forEach(axis => {
        const data = this.scroll[axis];

        info[axis + 'Position'] = data.position;
        info[axis + 'Percentage'] = data.percentage;
        info[axis + 'Size'] = data.size;
        info[axis + 'ContainerSize'] = this.container[axis];
      });

      return info;
    },
    updateScroll() {
      let change = false;
      const position = {
        top: this.$refs.container && this.$refs.container.scrollTop,
        left: this.$refs.container && this.$refs.container.scrollLeft,
      };

      if (this.scroll.vertical.position !== position.top) {
        this.scroll.vertical.position = position.top;
        change = true;
      }

      if (this.scroll.horizontal.position !== position.left) {
        this.scroll.horizontal.position = position.left;
        change = true;
      }

      change === true && this.startTimer();
    },
    async updateScrollSize() {
      await this.$nextTick();
      await this.$nextTick(); // wait for rerender

      if (!this.$refs.content) return;

      const { offsetWidth: width, offsetHeight: height } = this.$refs.content;

      if (this.scroll.horizontal.size !== width) {
        this.scroll.horizontal.size = width;
        this.startTimer();
      }

      if (this.scroll.vertical.size !== height) {
        this.scroll.vertical.size = height;
        this.startTimer();
      }
    },
    async updateContainer() {
      await this.$nextTick();
      await this.$nextTick(); // wait for rerender

      if (!this.$refs.container) return;

      const { offsetWidth: width, offsetHeight: height } = this.$refs.container;
      let change = false;

      if (this.container.vertical !== height) {
        this.container.vertical = height;
        change = true;
      }

      if (this.container.horizontal !== width) {
        this.container.horizontal = width;
        change = true;
      }

      change === true && this.startTimer();
    },
    startTimer() {
      if (this.tempShowing === true) {
        clearTimeout(this.timer);
      } else {
        this.tempShowing = true;
      }

      this.timer = setTimeout(() => { this.tempShowing = false; }, this.delay);
      this.$listeners.scroll && this.emitScroll();
    },
    setForceDenseGuideV(payload) {
      this.forceDenseGuideV = payload;
    },
    setForceDenseGuideH(payload) {
      this.forceDenseGuideH = payload;
    },
  },
};
